class SmartProductButton extends HTMLElement {
  /**
   * Constructor del componente SmartProductButton
   * Extiende HTMLElement para crear un componente web personalizado
   */
  constructor() {
    super();
  }

  /**
   * Inicializa el componente cuando se añade al DOM
   * Configura propiedades y establece delegación de eventos
   */
  connectedCallback() {
    this.productUrl = this.dataset.productUrl;
    this.productHandle = this.dataset.productHandle;
    this.variantId = this.dataset.variantId;
    this.dataStock = parseInt(this.dataset.stock);
    this.productTitle = this.dataset.productTitle;

    // Handler único delegado para todos los botones con data-action
    this.onClick = (event) => {
      const btn = event.target.closest('[data-action]');
      if (!btn) return;
      this.handleEvent(btn);
    };

    this.addEventListener('click', this.onClick);
  }

  /**
   * Limpia los event listeners cuando el componente se elimina del DOM
   * para evitar memory leaks
   */
  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  /**
   * Maneja las acciones según el atributo data-action del elemento
   * Soporta: load-volumen-modal, load-variant-modal, load-line-items-modal y minus-quantity
   * @param {HTMLElement} element - Elemento con atributo data-action
   */
  handleEvent(element) {
      const action = element.dataset.action;
      if (action === 'load-volumen-modal') {
          this.loadVolumenModal()
      }

      if (action === 'load-variant-modal') {
          this.loadVariantModal()
      }

      if (action === 'load-line-items-modal') {
          this.loadItemsModal()
      }

      if (action === 'minus-quantity') {
        this.handleUpdateCart('minus')
      }
  }

  /**
   * Carga el modal para productos con compra por volumen
   * Delega al método central loadModalContent
   */
  loadVolumenModal() {
    this.loadModalContent('main-product');
  }

  /**
   * Carga el modal para productos con variantes
   * Delega al método central loadModalContent
   */
  loadVariantModal() {
    this.loadModalContent('main-product');
  }

  /**
   * Carga el modal para mostrar line items del carrito
   * Delega al método central loadModalContent
   */
  loadItemsModal() {
    this.loadModalContent('line-items');
  }

  /**
   * Método central para cargar contenido en el modal
   * Preserva parámetros de URL, realiza fetch y delega el procesamiento según el tipo
   * @param {string} sectionType - Tipo de sección ('main-product' o 'line-items')
   */
  loadModalContent(sectionType) {
    // Obtener la URL base del producto
    const baseUrl = this.productUrl;
    
    // Crear un objeto URL para manipular la URL de manera segura
    const url = new URL(baseUrl, window.location.href);
    
    // Preservar todos los parámetros de consulta existentes de la URL actual
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((value, key) => {
      // Solo añadir si no existe ya en la URL del producto
      if (!url.searchParams.has(key)) {
        if(key == 'country') return;
        url.searchParams.set(key, value);
      }
    });
    
    // Añadir el parámetro section_id según el tipo
    url.searchParams.set('section_id', sectionType);
    
    fetch(url.toString())
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then(html => {
        // Procesar la respuesta según el tipo de sección
        if (sectionType === 'main-product') {
          this.handleMainProductResponse(html);
        } else if (sectionType === 'line-items') {
          this.handleLineItemsResponse(html);
        }
      })
      .catch(error => {
        console.error('Error loading modal content:', error);
      });
  }

  /**
   * Actualiza la cantidad de un producto en el carrito
   * Actualiza UI inmediatamente y emite evento para actualización real con debounce
   * @param {'plus'|'minus'} action - Acción a realizar
   */
  handleUpdateCart(action) {
    // Calculamos la nueva cantidad según la acción
    const quantitySelector = this.querySelector('[data-quantity-selector]');
    const currentQuantity = parseInt(quantitySelector.textContent);
    const newQuantity = action === 'plus' ? currentQuantity + 1 : currentQuantity - 1;

    // Validamos que la cantidad esté dentro de los límites permitidos (0 a stock máximo)
    if(
      newQuantity < 0 || 
      newQuantity > parseInt(this.dataStock)
    ){
      return;
    }

    // Actualizamos la UI inmediatamente para feedback visual
    quantitySelector.textContent = newQuantity;

    // Preparamos los datos para el evento de actualización
    const detail = {
      type: 'update-quantity',
      variant_id: this.variantId,
      product_id: null,
      product_title: this.productTitle,
      quantity: newQuantity,
      line_index: null
    };

    // Debounce: cancelamos timeout previo si existe
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    // Programamos la actualización con delay para evitar múltiples peticiones
    this.updateTimeout = setTimeout(() => {
      this.lockButtons();

      // Emitimos evento para que card-product maneje la actualización del carrito
      this.dispatchEvent(new CustomEvent('pb:card:update', {
        detail,
        bubbles: true,
      }));
    }, 500);
  }

  /**
   * Deshabilita todos los botones del componente durante la actualización
   * Card-product se encargará de re-renderizar el componente después
   */
  lockButtons() {
    this.querySelectorAll('button').forEach(button => {
      button.setAttribute('disabled', 'disabled');
      button.classList.add('disabled');
    });
  }

  /**
   * Procesa la respuesta HTML para la sección main-product y actualiza el modal
   * Extrae y actualiza slider, header y contenido principal
   * @param {string} html - Contenido HTML de la respuesta
   */
  handleMainProductResponse(html) {
    // Parsear el HTML de respuesta
    const doc = new DOMParser().parseFromString(html, 'text/html');
    
    // Extraer los elementos necesarios
    const mainSlider = doc.querySelector('.product__media');
    const mainHeader = doc.querySelector('.c-product__header');
    const mainProductInfo = doc.querySelector('.c-product__info');
    
    // Verificar que tenemos todos los elementos necesarios
    if (!mainSlider || !mainHeader || !mainProductInfo) {
      console.warn('No se encontraron todos los elementos necesarios en la respuesta');
      return;
    }
    
    // Eliminar el encabezado duplicado si existe
    mainProductInfo.querySelector('.c-product__header')?.remove();
    
    // Obtener referencias a los contenedores en el modal
    const modal = document.querySelector('#modal-cards');
    if (!modal) {
      console.error('No se encontró el modal en el DOM');
      return;
    }
    
    const slider = modal.querySelector('.slider-mobile-gutter');
    const header = modal.querySelector('.c-modal__content__header');
    const content = modal.querySelector('.c-modal__content__form');
    
    // Ocultar/mostrar las secciones correspondientes
    modal.querySelector('#content-line-items')?.classList.add('hidden');
    modal.querySelector('#content-main-product')?.classList.remove('hidden');
    
    // Actualizar el contenido del modal
    if (slider && mainSlider) slider.innerHTML = mainSlider.innerHTML;
    if (header && mainHeader) header.innerHTML = mainHeader.innerHTML;
    if (content && mainProductInfo) content.innerHTML = mainProductInfo.innerHTML;
    
    // Mostrar el modal
    modal.show(this.querySelector('button'));
  }

  /**
   * Procesa la respuesta HTML para la sección line-items y actualiza el modal
   * Actualiza el contenedor de line-items y muestra la sección correspondiente
   * @param {string} html - Contenido HTML de la respuesta
   */
  handleLineItemsResponse(html) {
    // Parsear el HTML de respuesta
    const doc = new DOMParser().parseFromString(html, 'text/html');
    
    // Extraer los elementos necesarios
    const lineItems = doc.querySelector('#line-items-modal');
    
    // Verificar que tenemos el elemento necesario
    if (!lineItems) {
      console.warn('No se encontró el contenido de line items en la respuesta');
      return;
    }
    
    // Obtener referencia al modal
    const modal = document.querySelector('#modal-cards');
    if (!modal) {
      console.error('No se encontró el modal en el DOM');
      return;
    }
    
    // Actualizar el contenido del modal
    const contentContainer = modal.querySelector('#content-line-items');
    if (contentContainer) contentContainer.innerHTML = lineItems.innerHTML;
    
    // Ocultar/mostrar las secciones correspondientes
    modal.querySelector('#content-main-product')?.classList.add('hidden');
    modal.querySelector('#content-line-items')?.classList.remove('hidden');
    
    // Mostrar el modal
    modal.show(this.querySelector('button'));
  }
}

customElements.define('smart-product-button', SmartProductButton);